import { type ReactNode } from "react";

import {
  CheckboxGroup as ReactAriaCheckboxGroup,
  type CheckboxGroupProps as ReactAriaCheckboxGroupProps,
} from "react-aria-components";

import { FormFieldAssistiveText } from "#components/shared/FormFieldAssistiveText";
import { FormFieldHeader } from "#components/shared/FormFieldHeader";
import { cn } from "#utils";

/**
 * A group component for managing multiple related checkboxes with shared state.
 *
 * Supports two patterns for rendering children:
 *
 * @example Static children pattern - simple and declarative
 * ```tsx
 * <CheckboxGroup label="Notifications" defaultValue={["email"]}>
 *   <Checkbox value="email">Email notifications</Checkbox>
 *   <Checkbox value="sms">SMS notifications</Checkbox>
 *   <Checkbox value="push">Push notifications</Checkbox>
 * </CheckboxGroup>
 * ```
 *
 * @example Render prop pattern - access group state for conditional rendering
 * ```tsx
 * <CheckboxGroup label="Features" isDisabled={isLoading}>
 *   {({ isDisabled, isInvalid }) => (
 *     <>
 *       <Checkbox value="feature1">
 *         Feature 1 {isDisabled && "(loading...)"}
 *       </Checkbox>
 *       <Checkbox value="feature2">
 *         Feature 2 {isInvalid && <ErrorIcon />}
 *       </Checkbox>
 *     </>
 *   )}
 * </CheckboxGroup>
 * ```
 */
export interface CheckboxGroupProps extends ReactAriaCheckboxGroupProps {
  /** Label text for the checkbox group */
  label?: string;
  /** Assistive text displayed below the group */
  assistiveText?: ReactNode;
  /** Tooltip text displayed next to the label */
  tooltipText?: ReactNode;
  /** Additional class name for the group container */
  className?: string;
  /** Layout orientation for the checkboxes */
  orientation?: "horizontal" | "vertical";
}

export function CheckboxGroup({
  label,
  assistiveText,
  tooltipText,
  className,
  isDisabled,
  isInvalid,
  orientation = "vertical",
  children,
  ...props
}: CheckboxGroupProps) {
  return (
    <ReactAriaCheckboxGroup
      className={cn("flex flex-col gap-[.625rem]", className)}
      isDisabled={isDisabled}
      isInvalid={isInvalid}
      {...props}
    >
      {/*
        Using render props pattern to access group state (isDisabled, isInvalid, etc.)
        from React Aria. This allows us to style child elements based on group state
        without prop drilling or additional context.
      */}
      {(renderProps) => (
        <>
          <FormFieldHeader
            isDisabled={renderProps.isDisabled}
            label={label}
            tooltipText={tooltipText}
          />
          <div
            className={cn(
              "flex gap-xl",
              orientation === "horizontal" ? "flex-row flex-wrap" : "flex-col",
            )}
          >
            {/*
              Support both static children and render prop pattern.
              Static: <CheckboxGroup><Checkbox /></CheckboxGroup>
              Render prop: <CheckboxGroup>{(state) => <Checkbox />}</CheckboxGroup>
            */}
            {typeof children === "function" ? children(renderProps) : children}
          </div>
          <FormFieldAssistiveText
            isDisabled={renderProps.isDisabled}
            isInvalid={renderProps.isInvalid}
          >
            {assistiveText}
          </FormFieldAssistiveText>
        </>
      )}
    </ReactAriaCheckboxGroup>
  );
}
